import { useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Hint } from "@/ui2/atoms/Hint";
import { isUserPeerOrCM } from "@/utils/authToken";
import { Link2 } from "@/ui2/atoms/Link";
import { ModalControlled2 } from "@/ui2/molecules/Modal";
import type { Preferences } from "@/core/types";
import { toTitleCase } from "@/utils/filters";

import { CalendarPreferencesModal } from "./CalendarPreferencesModal";

type CalendarPreferencesProps = {
  isReadOnly?: boolean;
  onUpdate?: (preferences: Preferences) => Promise<void>;
  preferences?: Preferences;
};

const DEFAULT_CALENDAR_PREFERENCES: Preferences = {
  calendar: { events: ["scheduled"], view: "DAY" },
  time_zone: "US/Eastern",
};

function mergeWithDefaults(preferences?: Preferences) {
  if (!preferences) return DEFAULT_CALENDAR_PREFERENCES;

  return {
    ...DEFAULT_CALENDAR_PREFERENCES,
    ...preferences,
    calendar: {
      ...DEFAULT_CALENDAR_PREFERENCES.calendar,
      ...preferences.calendar,
    },
  };
}

export function CalendarPreferences({
  isReadOnly,
  onUpdate,
  preferences,
}: CalendarPreferencesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const mergedPreferences = mergeWithDefaults(preferences);
  const calendarView = toTitleCase(mergedPreferences.calendar?.view || "DAY");

  return (
    <>
      <DashboardCard2
        Actions={
          !isReadOnly && (
            <Button2
              onPress={() => setIsModalOpen(true)}
              size="sm"
              variant="link"
              {...getAutomationDataAttr("CalendarPreferencesEditButton")}
            >
              Edit
            </Button2>
          )
        }
        id="scroll-to-preferences"
        title="Calendar Preferences"
      >
        <div data-public="true">
          <div className="tw:mb-4">
            <p className="tw:mb-1 tw:text-sm tw:font-bold">Time Zone</p>

            <Hint className="tw:mb-2">
              To update your time zone, contact{" "}
              <Link2 href="mailto:support@aptihealth.com" isExternal size="xs">
                support@aptihealth.com
              </Link2>
            </Hint>

            <span {...getAutomationDataAttr("ProviderTimeZone")} className="tw:text-muted">
              {mergedPreferences.time_zone}
            </span>
          </div>

          <div className="tw:mb-4">
            <p className="tw:mb-1 tw:text-sm tw:font-bold">Default Calendar View</p>
            <span {...getAutomationDataAttr("CalendarView")}>{calendarView}</span>
          </div>

          {!isUserPeerOrCM() && (
            <div className="tw:mb-4">
              <p className="tw:mb-1 tw:text-sm tw:font-bold">Events Shown by Default</p>

              <Hint className="tw:mb-2">
                Choose which types of events appear on your calendar by default. You can update this
                view anytime using filters.
              </Hint>

              <span {...getAutomationDataAttr("CalendarEvents")}>
                <ul>
                  {(mergedPreferences.calendar?.events || []).map((event) => (
                    <li className="tw:flex" key={"provider-preferences-event-" + event}>
                      {/* TODO: use replaceAll("_", " ") — replace() only removes the first underscore */}
                      {toTitleCase(event.replace("_", " "))}
                    </li>
                  ))}
                </ul>
              </span>
            </div>
          )}
        </div>
      </DashboardCard2>

      {!isReadOnly && onUpdate && (
        <ModalControlled2
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          title="Calendar Preferences"
        >
          <CalendarPreferencesModal preferences={mergedPreferences} updatePreferences={onUpdate} />
        </ModalControlled2>
      )}
    </>
  );
}
